/**
 * Le créneau d'un jour travaillé et son heure de rendez-vous (lot T0, 2026-10-25), dans le briefing d'une intervention.
 *
 * Décision de Benjamin (registre B33) : matin / après-midi, heure de rendez-vous facultative. Partagés par toute l'équipe du jour.
 * Le choix ne s'écrit qu'au clic sur « Enregistrer » (une écriture, la révision en jeton) ; un refus relit PUIS se dit.
 */
import { useEffect, useState } from 'react';

import { ApiError, api, type CreneauJour as Creneau } from '../api/client';
import { LIBELLE_CRENEAU, choixCreneauModifie, lireChoixCreneau, rendezVousFr } from '../domaine/planning-interventions';

export function CreneauJour({
  interventionId,
  revision,
  jour,
  slot,
  appointmentTime,
  peutGerer,
  onChange,
  onMessage,
}: {
  interventionId: string;
  revision: number;
  jour: string;
  slot: Creneau | null;
  appointmentTime: string | null;
  peutGerer: boolean;
  onChange: () => void;
  onMessage: (message: string) => void;
}) {
  const [choix, setChoix] = useState({ slot: slot ?? '', heure: appointmentTime ?? '' });
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // La relecture rend les valeurs enregistrées : le formulaire repart d'elles.
  useEffect(() => setChoix({ slot: slot ?? '', heure: appointmentTime ?? '' }), [slot, appointmentTime]);

  if (!peutGerer) {
    const place = [slot === null ? '' : LIBELLE_CRENEAU[slot], rendezVousFr(appointmentTime)].filter((x) => x !== '').join(' · ');
    return place === '' ? null : <span className="briefing-creneau"> — {place}</span>;
  }

  const lu = lireChoixCreneau(choix);
  const modifie = lu.ok && choixCreneauModifie(lu.valeur, { slot, appointmentTime });

  const enregistrer = async () => {
    if (!lu.ok) {
      setErreur(lu.erreur);
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      await api.definirCreneauIntervention(interventionId, revision, jour, lu.valeur);
      onMessage('Créneau enregistré.');
      onChange();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) onChange();
      setErreur(e instanceof ApiError || e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <span className="briefing-creneau saisie-ligne">
      <label>
        <span className="sr-only">Créneau</span>
        <select value={choix.slot} disabled={enCours} onChange={(e) => setChoix({ ...choix, slot: e.target.value })}>
          <option value="">Dans la journée</option>
          <option value="MORNING">{LIBELLE_CRENEAU.MORNING}</option>
          <option value="AFTERNOON">{LIBELLE_CRENEAU.AFTERNOON}</option>
        </select>
      </label>
      <label>
        <span className="aide">RDV</span>{' '}
        <input type="time" step={300} value={choix.heure} disabled={enCours} onChange={(e) => setChoix({ ...choix, heure: e.target.value })} aria-label="Heure de rendez-vous (facultative)" />
      </label>
      {modifie && (
        <button type="button" className="secondaire" disabled={enCours} onClick={() => void enregistrer()}>
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      )}
      {!lu.ok && <span className="message erreur en-ligne">{lu.erreur}</span>}
      {erreur && <span className="message erreur en-ligne">{erreur}</span>}
    </span>
  );
}
